
import { useRef, useState } from 'react'
import { getNodesBounds, getViewportForBounds, useReactFlow } from '@xyflow/react'
import { SIGNAL_CATEGORIES, SIGNAL_META } from '@/lib/signals'
import { Icon } from '@/components/ui/Icon'
import { btn, cx } from '@/lib/ui'
import { fileStem } from '@/lib/run-sheet'
import { useDetailsPopover } from '@/lib/use-details-popover'
import { errorMessage } from '@/lib/client-api'

/**
 * Schematic export. Renders the whole graph (not just the visible part) to a
 * high-resolution bitmap with html-to-image, then either downloads it as PNG
 * or lays it on an A3 landscape page with a title block using jsPDF. Both
 * libraries load on demand so the canvas bundle stays small.
 */

const A3 = { w: 420, h: 297 } // mm, landscape
const MARGIN = 12
const TITLE_BLOCK = 22

export function ExportMenu({
  projectName,
  workspaceName,
  nodeCount,
  cableCount,
}: {
  projectName: string
  workspaceName: string
  nodeCount: number
  cableCount: number
}) {
  const { getNodes } = useReactFlow()
  const [busy, setBusy] = useState<null | 'png' | 'pdf'>(null)
  const [error, setError] = useState<string | null>(null)
  const menuRef = useRef<HTMLDetailsElement>(null)
  const closeMenu = () => menuRef.current?.removeAttribute('open')

  async function render(pixelRatio: number) {
    const nodes = getNodes()
    if (nodes.length === 0) throw new Error('Nothing to export yet — place some equipment first.')
    const viewport = document.querySelector<HTMLElement>('.react-flow__viewport')
    if (!viewport) throw new Error('Canvas not ready')

    const bounds = getNodesBounds(nodes)
    const pad = 48
    const width = Math.ceil(bounds.width + pad * 2)
    const height = Math.ceil(bounds.height + pad * 2)
    const transform = getViewportForBounds(bounds, width, height, 1, 1, pad)

    // Browsers cap a canvas by both its longest side and its total area, and
    // past either one they hand back a blank image or hang the tab rather than
    // raising — which is exactly when a real show is being handed to the crew.
    // Scale the request down to fit instead, and say so afterwards.
    const MAX_SIDE = 8192
    const MAX_AREA = 16_000_000
    const ratio = Math.max(
      1,
      Math.min(pixelRatio, MAX_SIDE / Math.max(width, height), Math.sqrt(MAX_AREA / (width * height))),
    )
    const reduced = ratio < pixelRatio

    const { toPng } = await import('html-to-image')
    // The schematic is printed and handed round on site, so it is drawn in
    // the light palette whatever the screen is set to. The attribute picks
    // up a stylesheet block of light values (see globals.css) while
    // html-to-image reads the computed styles; only the canvas changes.
    const flow = viewport.closest<HTMLElement>('.react-flow') ?? viewport
    flow.dataset.capture = 'light'
    let dataUrl: string
    try {
      dataUrl = await toPng(viewport, {
        width,
        height,
        pixelRatio: ratio,
        backgroundColor: '#ffffff',
        style: {
          width: `${width}px`,
          height: `${height}px`,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
        },
        // Controls, minimap and selection chrome are outside the viewport
        // element, but hidden edges (signal filters) are inside it: keep them
        // out so the export matches what is on screen.
        filter: (element) => !(element instanceof HTMLElement && element.classList?.contains('react-flow__edge') && element.style.display === 'none'),
      })
    } finally {
      delete flow.dataset.capture
    }
    return { dataUrl, width, height, reduced }
  }

  async function exportPng() {
    closeMenu()
    setBusy('png')
    setError(null)
    try {
      const { dataUrl, reduced } = await render(2)
      download(dataUrl, `${fileStem(projectName)}-schematic.png`)
      if (reduced) setError('This show is large, so the schematic was exported at a lower resolution.')
    } catch (cause) {
      setError(errorMessage(cause, 'Export failed'))
    } finally {
      setBusy(null)
    }
  }

  async function exportPdf() {
    closeMenu()
    setBusy('pdf')
    setError(null)
    try {
      const { dataUrl, width, height, reduced } = await render(2.5)
      const { jsPDF } = await import('jspdf')
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3', compress: true })

      // Title block along the top.
      pdf.setFillColor(17, 17, 17)
      pdf.rect(0, 0, A3.w, TITLE_BLOCK, 'F')
      pdf.setTextColor(255, 255, 255)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(15)
      pdf.text(projectName, MARGIN, 9.5)
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8.5)
      const stamp = new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
      pdf.text(
        `${workspaceName} · ${nodeCount} devices · ${cableCount} cables · schematic generated ${stamp} · ROUTR`,
        MARGIN,
        16,
      )

      // Legend, right-aligned in the title block.
      let x = A3.w - MARGIN
      for (const signal of [...SIGNAL_CATEGORIES].reverse()) {
        const label = SIGNAL_META[signal].label
        const w = pdf.getTextWidth(label)
        x -= w
        pdf.setTextColor(255, 255, 255)
        pdf.text(label, x, 13)
        // A dashed sample, matching the stroke pattern the drawing uses, so
        // the legend still reads on a greyscale print of the schematic.
        const [r, g, b] = hexToRgb(SIGNAL_META[signal].ink)
        pdf.setDrawColor(r, g, b)
        pdf.setLineWidth(1)
        const dash = SIGNAL_META[signal].dash
        pdf.setLineDashPattern(dash ? dash.split(' ').map((n) => Number(n) * 0.5) : [], 0)
        pdf.line(x - 8, 12.2, x - 2, 12.2)
        pdf.setLineDashPattern([], 0)
        x -= 14
      }

      // Drawing area, image scaled to fit while keeping its aspect ratio.
      const areaW = A3.w - MARGIN * 2
      const areaH = A3.h - TITLE_BLOCK - MARGIN * 2
      const scale = Math.min(areaW / width, areaH / height)
      const drawW = width * scale
      const drawH = height * scale
      const drawX = MARGIN + (areaW - drawW) / 2
      const drawY = TITLE_BLOCK + MARGIN + (areaH - drawH) / 2
      pdf.setFillColor(246, 247, 250)
      pdf.rect(MARGIN, TITLE_BLOCK + MARGIN, areaW, areaH, 'F')
      pdf.addImage(dataUrl, 'PNG', drawX, drawY, drawW, drawH, undefined, 'FAST')

      pdf.save(`${fileStem(projectName)}-schematic.pdf`)
      if (reduced) setError('This show is large, so the schematic was drawn at a lower resolution.')
    } catch (cause) {
      setError(errorMessage(cause, 'Export failed'))
    } finally {
      setBusy(null)
    }
  }

  const menuItem =
    'flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm transition-colors hover:bg-hover disabled:opacity-50'

  useDetailsPopover(menuRef)


  return (
    <details ref={menuRef} className="relative hidden md:block">
      <summary
        className={cx(btn.sm, btn.secondary, 'cursor-pointer list-none [&::-webkit-details-marker]:hidden')}
        title="Export the schematic as PDF or PNG"
        aria-haspopup="true"
        aria-label="Export schematic"
      >
        <Icon name="download" className="h-3.5 w-3.5" />
        <span className="hidden 2xl:inline">{busy ? 'Exporting…' : 'Export'}</span>
      </summary>
      <div className="glass absolute right-0 top-full z-40 mt-2 w-60 rounded-2xl p-1.5">
        <button type="button" onClick={exportPdf} disabled={busy !== null} className={menuItem}>
          <Icon name="download" className="h-4 w-4 text-muted" />
          <span>
            <span className="block">PDF</span>
            <span className="block text-[11px] text-muted">A3 landscape with title block</span>
          </span>
        </button>
        <button type="button" onClick={exportPng} disabled={busy !== null} className={menuItem}>
          <Icon name="image" className="h-4 w-4 text-muted" />
          <span>
            <span className="block">PNG</span>
            <span className="block text-[11px] text-muted">High-resolution bitmap</span>
          </span>
        </button>
        {error && (
          <p role="alert" className="px-2.5 py-1.5 text-[11px] text-danger">
            {error}
          </p>
        )}
      </div>
    </details>
  )
}

function download(dataUrl: string, filename: string) {
  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  link.click()
}

function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '')
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)]
}
